// =======================================================================
//
// THIS FILE SHOULD BE KEPT IN SYNC WITH THE BE.
//
// Any changes to this file should be reflected in it's copy in the FE
// The file currently sits in:
// src/rfq/sharedLogic.ts
//
// =======================================================================

import {
    AccountCurrencyCode,
    AircraftTypeMethod,
    RoadFuel,
    RoadVehicleType,
    ShippingCountryCode,
    TradeLane,
    TransitCorridor as ApiTransitCorridor,
} from '@lune-climate/lune'
import { assertType } from '@lune-fe/lune-components-lib'
import { Big } from 'big.js'
import { CsvError, parse as parseCsv } from 'csv-parse/sync'
import { CellValue } from 'exceljs'
import moment from 'moment'
import { Err, None, Ok, Option, Result, Some } from 'ts-results-es'

export type TableElem = string
export type TableRow = TableElem[]
export type Table = TableRow[]

export const accountCurrencyCodeArray: AccountCurrencyCode[] = Object.values(AccountCurrencyCode)

/**
 * Types used to perform operations on a sheet regardless of the origin of the
 * underlying data (CSV, Excel etc.). Since the initial implementation of our
 * mappings was tighly tied to Excel sheets, the interface is derived from the
 * features used from our Excel library.
 */
export type SimplifiedCell = { value: CellValue }
export type MappingDataSheet = {
    getRow(index: number): {
        values: CellValue[] | { [key: string]: CellValue }
        eachCell(callback: (cell: SimplifiedCell, colNumber: number) => void): void
    }
    getColumn(index: number): { values: readonly CellValue[] }
    findCell(n: number, c: number): SimplifiedCell | undefined
    name: string
    rowCount: number
}

/**
 * Helper class that allows a CSV file to behave the same manner as a Excel sheet.
 * The class performs no validation on inputs and expects all input indexes to be
 * valid. This is to be used in the FE only since the BE has it's own data format
 * that it uses during processing (initially implemented due to memory constraints).
 *
 * All indexes are 1-based, same behaviour as our Excel library. Additionally, the
 * library always returns the first element in getRow/getColumn as empty (I believe
 * done to adhere to 1-based indexes).
 *
 * TODO unify work data types for the FE and BE
 */
export class CsvSheet implements MappingDataSheet {
    private data: Table
    public name = '' // Not directly used for CSV's
    public rowCount

    constructor(data: string[][]) {
        this.data = data
        this.rowCount = data.length
    }

    getRow(index: number): {
        values: CellValue[]
        eachCell(callback: (cell: SimplifiedCell, colNumber: number) => void): void
    } {
        const row = this.data[index - 1] ?? []
        const adaptedRow = [undefined, ...row]
        return {
            values: adaptedRow,
            eachCell(callback: (cell: SimplifiedCell, colNumber: number) => void) {
                adaptedRow.forEach((value, colIndex) => callback({ value }, colIndex))
            },
        }
    }

    getColumn(index: number): { values: CellValue[] } {
        return { values: [undefined, ...this.data.map((e) => e[index - 1])] }
    }

    findCell(r: number, c: number): SimplifiedCell | undefined {
        return { value: this.data[r - 1][c - 1] }
    }
}

/**
 * Validates a string is a valid CSV and returns an array of string arrays with the CSV's content
 *
 * In the process of parsing the string, we must determine the original delimiter of the file.
 * Here we assume the first appearance of [',', ';', '\t'] is the delimiter. This is a limitation
 * in that if the first header of the CSV contains a `,` or `;` substring then we will
 * incorrectly determine that to be the delimiter.
 *
 * @param data - Text representation of the CSV file
 */
export function verifyCSV(
    data: string,
): Result<{ table: Table; delimiter: string | undefined }, string> {
    // Assume the first of ',', ';', '\t' is the delimiter
    // The CSV delimiter may change depending on a computer's regional settings.
    // https://answers.microsoft.com/en-us/msoffice/forum/all/excel-changes-my-delimiter-in-csv-although-my/16c9e389-5bdd-427b-bc8e-798961b4f535
    const delimiter = data.match(/[,;\t]/)?.[0]

    let csv: unknown[][]
    try {
        csv = parseCsv(data, { columns: false, delimiter }) as unknown[][]
    } catch (e) {
        if (e instanceof CsvError) {
            return Err(e.toString())
        }
        throw e
    }

    const table: Table = []
    for (const row of csv) {
        const tableRow: TableRow = []
        for (const item of row) {
            if (typeof item !== 'string') {
                // parseCsv should parse all null values into '', therefore we should expect all
                // values to be strings
                return Err(`Unexpected item in CSV: ${item}`)
            }
            tableRow.push(item)
        }
        table.push(tableRow)
    }
    return Ok({ table, delimiter })
}

/**
 * There are a lot of possible encodings for CSV files. Since we don't have direct
 * information from the user about the encoding, not even on some clues to help us
 * like locale, we need to make some assumptions. After some investigation and
 * discussion, the best course of action is to be use `utf-8` as the default since
 * it's the norm and used extensively nowadays. Since we've detected some legitimate
 * cases where this failed, a fallback to `windows-1252` was added. This encoding
 * is still widely used on english locale Excel exports.
 *
 * This does mean that we might be misdecoding/mangling data, but we have accepted
 * this possibility:
 * - We still do checks for a valid CSV which should catch the biggest misdecoding
 *   problems.
 * - Creating a template shows the underlying data so the customer might catch a
 *   wrong decoding at this stage.
 * - We prefer to mangle a bit of data in some fields but process the file than
 *   completely fail to process it.
 *
 * The list here is in ascending order of preference.
 */
const csvEncodings = ['utf-8', 'windows-1252'] as const

/**
 * Parses CSV data into a Table type object.
 * The CSV is expected to have a header row at the position specified in the template.
 *
 * @param headerRow - Header row number as present on the template (1-based)
 */
export async function csvToTable(
    rawCsvData: Uint8Array,
    headerRow: number,
): Promise<Result<Table, string>> {
    let data: string | undefined
    for (const encoding of csvEncodings) {
        try {
            data = new TextDecoder(encoding, { fatal: true }).decode(rawCsvData)
            break
        } catch (err) {
            if (err instanceof TypeError) {
                continue
            }
            throw err
        }
    }

    if (data === undefined) {
        return Err('Could not decode file')
    }

    return verifyCSV(data).andThen(({ table }) => {
        return headerRow >= table.length
            ? Err(`Header row ${headerRow} is beyond CSV data (${table.length} rows)`)
            : Ok(table.slice(headerRow))
    })
}

export type RfqTemplateData = {
    sheetName: string
    /**
     * The header row to use.
     */
    headerRow: number
    /**
     * The load mapping to use.
     */
    load: PropertyMapping<LoadUnit>
    /**
     * Load size information (TEU's). This is only used when `load` property is not `TEU`.
     */
    loadSize?: PropertyMapping<number>
    /**
     * Shipment time to store in the estimate.
     */
    shippedAt?: PropertyMapping<string>
    /**
     * Shipment ID to track CSV shipments results.
     */
    shipmentId?: PropertyMapping<string>
    /**
     * Whether shipmentId should be used as idempotency key
     */
    shipmentIdAsIdempotencyKey?: PropertyMapping<boolean>
    /**
     * Client account name to be used in the calculation.
     */
    clientAccountName?: PropertyMapping<string>
    /**
     * Currency of the client account. Used to selecting account or creating it
     */
    clientAccountCurrency?: PropertyMapping<AccountCurrencyCode>
    /**
     * The legs mapping to use.
     */
    legs: TemplateLegInfo[]
    /**
     * The row header as it was inferred from the template.
     *
     * May be missing because it either wasn't inferred or we're dealing with a template
     * created before this property was introduced.
     */
    inferredHeaderRow?: number
    /**
     * The load mapping as it was inferred from the template.
     *
     * May be missing if we're dealing with a template created before this property was introduced.
     */
    inferredLoad?: PropertyMapping<LoadUnit>
    /**
     * The legs mapping as it was inferred from the template. Partial, since it's not guaranteed that the LLM will have
     * inferred all the properties.
     *
     * May be missing if we're dealing with a template created before this property was introduced.
     */
    inferredLegs?: Partial<{
        transportMode: Partial<TransportModeMapping>
        origin: RouteMapping
        destination: RouteMapping
    }>[]
}

export type TemplateLegInfo = {
    transportMode: TransportModeMapping
    origin: RouteMapping
    destination: RouteMapping
}

export const loadUnitsArray = ['t', 'kg', 'TEU'] as const
export type LoadUnit = (typeof loadUnitsArray)[number]

export const loadSizeUnitsArray = ['t', 'kg'] as const
export type LoadExtraUnit = (typeof loadSizeUnitsArray)[number]

export type TransportModeGroup = 'Sea' | 'Air' | 'Road' | 'Rail' | 'Inland waterways'

// Two different types of transport modes have been defined in the RFQ logic:
// - Simple methods are very simple and specific methods, using just an enum to describe the
//   method ("SimpleSeaShippingMethod", "SimpleShippingMethod" in the API). No extra
//   properties are expected to exist for these methods.
// - Complex methods most likely have additional properties and rely on shipping methods that
//   use our more advanced methodologies. To note the same transport mode can, depending on
//   if properties are mapped and present, vastly change how it behaves. As an example: we
//   might use "container_ship" as method but if properties are mapped and present might
//   identify the vessel, in the air we might just be saying it's a "plane" but if a flight
//   number exists might be able to track it. Of course other properties that are tied to
//   the methodology can also be present: fuel, emission standard etc.
//
// Three things to note:
// - Complex methods might fallback to simple methods if mappings or data is missing.
// - Complex methods can still be using our simple methodology internally.
// - Complex methods are not guaranteed to have properties. This effectively means that a
//   simple method can be seen as a subset of a generic one.
//
// Given the above and since in the future we might want simple methods to have properties,
// this check is not enforced in the types. To make it easier to read/understand the code
// and to have to make FE work easier, separation exists when defining the transport modes.
export const complexSeaTransportModeArray = ['Container Ship'] as const
export const simpleSeaTransportModeMappings = {
    'Bulk carrier, <10dwkt, HFO': 'sea_bulk_carrier_10dwkt_hfo',
    'Bulk carrier, <10dwkt, VLSFO': 'sea_bulk_carrier_10dwkt_vlsfo',
    'Bulk carrier, <10dwkt, MDO': 'sea_bulk_carrier_10dwkt_mdo',
    'Bulk carrier, 10-35dwkt, HFO': 'sea_bulk_carrier_35dwkt_hfo',
    'Bulk carrier, 10-35dwkt, VLSFO': 'sea_bulk_carrier_35dwkt_vlsfo',
    'Bulk carrier, 10-35dwkt, MDO': 'sea_bulk_carrier_35dwkt_mdo',
    'Bulk carrier, 35-60dwkt, HFO': 'sea_bulk_carrier_60dwkt_hfo',
    'Bulk carrier, 35-60dwkt, VLSFO': 'sea_bulk_carrier_60dwkt_vlsfo',
    'Bulk carrier, 35-60dwkt, MDO': 'sea_bulk_carrier_60dwkt_mdo',
    'Bulk carrier, 60-100dwkt, HFO': 'sea_bulk_carrier_100dwkt_hfo',
    'Bulk carrier, 60-100dwkt, VLSFO': 'sea_bulk_carrier_100dwkt_vlsfo',
    'Bulk carrier, 60-100dwkt, MDO': 'sea_bulk_carrier_100dwkt_mdo',
    'Bulk carrier, 100-200dwkt, HFO': 'sea_bulk_carrier_200dwkt_hfo',
    'Bulk carrier, 100-200dwkt, VLSFO': 'sea_bulk_carrier_200dwkt_vlsfo',
    'Bulk carrier, 100-200dwkt, MDO': 'sea_bulk_carrier_200dwkt_mdo',
    'Bulk carrier, >200dwkt, HFO': 'sea_bulk_carrier_over_200dwkt_hfo',
    'Bulk carrier, >200dwkt, VLSFO': 'sea_bulk_carrier_over_200dwkt_vlsfo',
    'Bulk carrier, >200dwkt, MDO': 'sea_bulk_carrier_over_200dwkt_mdo',
    'Chemical tanker, <5dwkt, HFO': 'sea_chemical_tanker_5dwkt_hfo',
    'Chemical tanker, <5dwkt, VLSFO': 'sea_chemical_tanker_5dwkt_vlsfo',
    'Chemical tanker, <5dwkt, MDO': 'sea_chemical_tanker_5dwkt_mdo',
    'Chemical tanker, 5-10dwkt, HFO': 'sea_chemical_tanker_10dwkt_hfo',
    'Chemical tanker, 5-10dwkt, VLSFO': 'sea_chemical_tanker_10dwkt_vlsfo',
    'Chemical tanker, 5-10dwkt, MDO': 'sea_chemical_tanker_10dwkt_mdo',
    'Chemical tanker, 10-20dwkt, HFO': 'sea_chemical_tanker_20dwkt_hfo',
    'Chemical tanker, 10-20dwkt, VLSFO': 'sea_chemical_tanker_20dwkt_vlsfo',
    'Chemical tanker, 10-20dwkt, MDO': 'sea_chemical_tanker_20dwkt_mdo',
    'Chemical tanker, 20-40dwkt, HFO': 'sea_chemical_tanker_40dwkt_hfo',
    'Chemical tanker, 20-40dwkt, VLSFO': 'sea_chemical_tanker_40dwkt_vlsfo',
    'Chemical tanker, 20-40dwkt, MDO': 'sea_chemical_tanker_40dwkt_mdo',
    'Chemical tanker, >40dwkt, HFO': 'sea_chemical_tanker_over_40dwkt_hfo',
    'Chemical tanker, >40dwkt, VLSFO': 'sea_chemical_tanker_over_40dwkt_vlsfo',
    'Chemical tanker, >40dwkt, MDO': 'sea_chemical_tanker_over_40dwkt_mdo',
    'General cargo, <5dwkt, HFO': 'sea_general_cargo_5dwkt_hfo',
    'General cargo, <5dwkt, VLSFO': 'sea_general_cargo_5dwkt_vlsfo',
    'General cargo, <5dwkt, MDO': 'sea_general_cargo_5dwkt_mdo',
    'General cargo, 5-10dwkt, HFO': 'sea_general_cargo_10dwkt_hfo',
    'General cargo, 5-10dwkt, VLSFO': 'sea_general_cargo_10dwkt_vlsfo',
    'General cargo, 5-10dwkt, MDO': 'sea_general_cargo_10dwkt_mdo',
    'General cargo, 10-20dwkt, HFO': 'sea_general_cargo_20dwkt_hfo',
    'General cargo, 10-20dwkt, VLSFO': 'sea_general_cargo_20dwkt_vlsfo',
    'General cargo, 10-20dwkt, MDO': 'sea_general_cargo_20dwkt_mdo',
    'General cargo, >20dwkt, HFO': 'sea_general_cargo_over_20dwkt_hfo',
    'General cargo, >20dwkt, VLSFO': 'sea_general_cargo_over_20dwkt_vlsfo',
    'General cargo, >20dwkt, MDO': 'sea_general_cargo_over_20dwkt_mdo',
    'Liquefied gas tanker, <50dwkt, HFO': 'sea_liquefied_gas_tanker_50dwkt_hfo',
    'Liquefied gas tanker, <50dwkt, VLSFO': 'sea_liquefied_gas_tanker_50dwkt_vlsfo',
    'Liquefied gas tanker, <50dwkt, MDO': 'sea_liquefied_gas_tanker_50dwkt_mdo',
    'Liquefied gas tanker, 50-100dwkt, HFO': 'sea_liquefied_gas_tanker_100dwkt_hfo',
    'Liquefied gas tanker, 50-100dwkt, VLSFO': 'sea_liquefied_gas_tanker_100dwkt_vlsfo',
    'Liquefied gas tanker, 50-100dwkt, MDO': 'sea_liquefied_gas_tanker_100dwkt_mdo',
    'Liquefied gas tanker, 100-200dwkt, HFO': 'sea_liquefied_gas_tanker_200dwkt_hfo',
    'Liquefied gas tanker, 100-200dwkt, VLSFO': 'sea_liquefied_gas_tanker_200dwkt_vlsfo',
    'Liquefied gas tanker, 100-200dwkt, MDO': 'sea_liquefied_gas_tanker_200dwkt_mdo',
    'Liquefied gas tanker, >200dwkt, HFO': 'sea_liquefied_gas_tanker_over_200dwkt_hfo',
    'Liquefied gas tanker, >200dwkt, VLSFO': 'sea_liquefied_gas_tanker_over_200dwkt_vlsfo',
    'Liquefied gas tanker, >200dwkt, MDO': 'sea_liquefied_gas_tanker_over_200dwkt_mdo',
    'Oil tanker, <5dwkt, HFO': 'sea_oil_tanker_5dwkt_hfo',
    'Oil tanker, <5dwkt, VLSFO': 'sea_oil_tanker_5dwkt_vlsfo',
    'Oil tanker, <5dwkt, MDO': 'sea_oil_tanker_5dwkt_mdo',
    'Oil tanker, 5-10dwkt, HFO': 'sea_oil_tanker_10dwkt_hfo',
    'Oil tanker, 5-10dwkt, VLSFO': 'sea_oil_tanker_10dwkt_vlsfo',
    'Oil tanker, 5-10dwkt, MDO': 'sea_oil_tanker_10dwkt_mdo',
    'Oil tanker, 10-20dwkt, HFO': 'sea_oil_tanker_20dwkt_hfo',
    'Oil tanker, 10-20dwkt, VLSFO': 'sea_oil_tanker_20dwkt_vlsfo',
    'Oil tanker, 10-20dwkt, MDO': 'sea_oil_tanker_20dwkt_mdo',
    'Oil tanker, 20-60dwkt, HFO': 'sea_oil_tanker_60dwkt_hfo',
    'Oil tanker, 20-60dwkt, VLSFO': 'sea_oil_tanker_60dwkt_vlsfo',
    'Oil tanker, 20-60dwkt, MDO': 'sea_oil_tanker_60dwkt_mdo',
    'Oil tanker, 60-80dwkt, HFO': 'sea_oil_tanker_80dwkt_hfo',
    'Oil tanker, 60-80dwkt, VLSFO': 'sea_oil_tanker_80dwkt_vlsfo',
    'Oil tanker, 60-80dwkt, MDO': 'sea_oil_tanker_80dwkt_mdo',
    'Oil tanker, 80-112dwkt, HFO': 'sea_oil_tanker_112dwkt_hfo',
    'Oil tanker, 80-112dwkt, VLSFO': 'sea_oil_tanker_112dwkt_vlsfo',
    'Oil tanker, 80-112dwkt, MDO': 'sea_oil_tanker_112dwkt_mdo',
    'Oil tanker, 112-200dwkt, HFO': 'sea_oil_tanker_200dwkt_hfo',
    'Oil tanker, 112-200dwkt, VLSFO': 'sea_oil_tanker_200dwkt_vlsfo',
    'Oil tanker, 112-200dwkt, MDO': 'sea_oil_tanker_200dwkt_mdo',
    'Oil tanker, >200dwkt, HFO': 'sea_oil_tanker_over_200dwkt_hfo',
    'Oil tanker, >200dwkt, VLSFO': 'sea_oil_tanker_over_200dwkt_vlsfo',
    'Oil tanker, >200dwkt, MDO': 'sea_oil_tanker_over_200dwkt_mdo',
    'Other liquids tanker, <1dwkt, HFO': 'sea_other_liquids_tanker_1dwkt_hfo',
    'Other liquids tanker, <1dwkt, VLSFO': 'sea_other_liquids_tanker_1dwkt_vlsfo',
    'Other liquids tanker, <1dwkt, MDO': 'sea_other_liquids_tanker_1dwkt_mdo',
    'Other liquids tanker, >1dwkt, HFO': 'sea_other_liquids_tanker_over_1dwkt_hfo',
    'Other liquids tanker, >1dwkt, VLSFO': 'sea_other_liquids_tanker_over_1dwkt_vlsfo',
    'Other liquids tanker, >1dwkt, MDO': 'sea_other_liquids_tanker_over_1dwkt_mdo',
    'Ropax, <2dwkt, HFO': 'sea_ropax_2dwkt_hfo',
    'Ropax, <2dwkt, VLSFO': 'sea_ropax_2dwkt_vlsfo',
    'Ropax, <2dwkt, MDO': 'sea_ropax_2dwkt_mdo',
    'Ropax, 2-5dwkt, HFO': 'sea_ropax_5dwkt_hfo',
    'Ropax, 2-5dwkt, VLSFO': 'sea_ropax_5dwkt_vlsfo',
    'Ropax, 2-5dwkt, MDO': 'sea_ropax_5dwkt_mdo',
    'Ropax, 5-10dwkt, HFO': 'sea_ropax_10dwkt_hfo',
    'Ropax, 5-10dwkt, VLSFO': 'sea_ropax_10dwkt_vlsfo',
    'Ropax, 5-10dwkt, MDO': 'sea_ropax_10dwkt_mdo',
    'Ropax, 10-20dwkt, HFO': 'sea_ropax_20dwkt_hfo',
    'Ropax, 10-20dwkt, VLSFO': 'sea_ropax_20dwkt_vlsfo',
    'Ropax, 10-20dwkt, MDO': 'sea_ropax_20dwkt_mdo',
    'Ropax, >20dwkt, HFO': 'sea_ropax_over_20dwkt_hfo',
    'Ropax, >20dwkt, VLSFO': 'sea_ropax_over_20dwkt_vlsfo',
    'Ropax, >20dwkt, MDO': 'sea_ropax_over_20dwkt_mdo',
    'Refrigerated bulk carrier, <2dwkt, HFO': 'sea_refrigerated_bulk_carrier_2dwkt_hfo',
    'Refrigerated bulk carrier, <2dwkt, VLSFO': 'sea_refrigerated_bulk_carrier_2dwkt_vlsfo',
    'Refrigerated bulk carrier, <2dwkt, MDO': 'sea_refrigerated_bulk_carrier_2dwkt_mdo',
    'Refrigerated bulk carrier, 2-6dwkt, HFO': 'sea_refrigerated_bulk_carrier_6dwkt_hfo',
    'Refrigerated bulk carrier, 2-6dwkt, VLSFO': 'sea_refrigerated_bulk_carrier_6dwkt_vlsfo',
    'Refrigerated bulk carrier, 2-6dwkt, MDO': 'sea_refrigerated_bulk_carrier_6dwkt_mdo',
    'Refrigerated bulk carrier, 6-10dwkt, HFO': 'sea_refrigerated_bulk_carrier_10dwkt_hfo',
    'Refrigerated bulk carrier, 6-10dwkt, VLSFO': 'sea_refrigerated_bulk_carrier_10dwkt_vlsfo',
    'Refrigerated bulk carrier, 6-10dwkt, MDO': 'sea_refrigerated_bulk_carrier_10dwkt_mdo',
    'Refrigerated bulk carrier, >10dwkt, HFO': 'sea_refrigerated_bulk_carrier_over_10dwkt_hfo',
    'Refrigerated bulk carrier, >10dwkt, VLSFO': 'sea_refrigerated_bulk_carrier_over_10dwkt_vlsfo',
    'Refrigerated bulk carrier, >10dwkt, MDO': 'sea_refrigerated_bulk_carrier_over_10dwkt_mdo',
    'Roro, <5dwkt, HFO': 'sea_roro_5dwkt_hfo',
    'Roro, <5dwkt, VLSFO': 'sea_roro_5dwkt_vlsfo',
    'Roro, <5dwkt, MDO': 'sea_roro_5dwkt_mdo',
    'Roro, 5-10dwkt, HFO': 'sea_roro_10dwkt_hfo',
    'Roro, 5-10dwkt, VLSFO': 'sea_roro_10dwkt_vlsfo',
    'Roro, 5-10dwkt, MDO': 'sea_roro_10dwkt_mdo',
    'Roro, 10-15dwkt, HFO': 'sea_roro_15dwkt_hfo',
    'Roro, 10-15dwkt, VLSFO': 'sea_roro_15dwkt_vlsfo',
    'Roro, 10-15dwkt, MDO': 'sea_roro_15dwkt_mdo',
    'Roro, >15dwkt, HFO': 'sea_roro_over_15dwkt_hfo',
    'Roro, >15dwkt, VLSFO': 'sea_roro_over_15dwkt_vlsfo',
    'Roro, >15dwkt, MDO': 'sea_roro_over_15dwkt_mdo',
    'Vehicle carrier, <30dwkt, HFO': 'sea_vehicle_carrier_30dwkt_hfo',
    'Vehicle carrier, <30dwkt, VLSFO': 'sea_vehicle_carrier_30dwkt_vlsfo',
    'Vehicle carrier, <30dwkt, MDO': 'sea_vehicle_carrier_30dwkt_mdo',
    'Vehicle carrier, 30-50dwkt, HFO': 'sea_vehicle_carrier_50dwkt_hfo',
    'Vehicle carrier, 30-50dwkt, VLSFO': 'sea_vehicle_carrier_50dwkt_vlsfo',
    'Vehicle carrier, 30-50dwkt, MDO': 'sea_vehicle_carrier_50dwkt_mdo',
    'Vehicle carrier, >50dwkt, HFO': 'sea_vehicle_carrier_over_50dwkt_hfo',
    'Vehicle carrier, >50dwkt, VLSFO': 'sea_vehicle_carrier_over_50dwkt_vlsfo',
    'Vehicle carrier, >50dwkt, MDO': 'sea_vehicle_carrier_over_50dwkt_mdo',
} as const
export const simpleSeaTransportModeArray = Object.keys(
    simpleSeaTransportModeMappings,
) as (keyof typeof simpleSeaTransportModeMappings)[]
export const seaTransportModeArray = [
    ...simpleSeaTransportModeArray,
    ...complexSeaTransportModeArray,
] as const

export const complexAirTransportModeArray = ['Cargo Plane', 'Passenger Plane', 'Plane'] as const
export const simpleAirTransportModeArray = [] as const
export const airTransportModeArray = [
    ...simpleAirTransportModeArray,
    ...complexAirTransportModeArray,
] as const

export const complexRoadTransportModeArray = [
    'Inferred truck',
    // To avoid breaking backwards compatibility, use the previous name for this transport
    // mode. The FE should display the correct name: `Van`
    'Truck',
    'Truck rigid 7.5t',
    'Truck rigid 12t',
    'Truck rigid 20t',
    'Truck rigid 26t',
    'Truck rigid 32t',
    'Truck articulated 34t',
    'Truck articulated 40t',
    'Truck articulated 44t',
    'Truck articulated 60t',
    'Truck articulated 72t',
    'Electric truck',
    'Electric van',
] as const
export const simpleRoadTransportModeArray = [] as const
export const roadTransportModeArray = [
    ...simpleRoadTransportModeArray,
    ...complexRoadTransportModeArray,
] as const

export const complexRailTransportModeArray = ['Rail', 'Electric train', 'Diesel train'] as const
export const simpleRailTransportModeMappings = {
    'Diesel freight train US average': 'diesel_freight_train_us_average',
    'Diesel freight train EU average': 'diesel_freight_train_eu_average',
    'Diesel freight train EU container': 'diesel_freight_train_eu_container',
    'Diesel freight train EU cars': 'diesel_freight_train_eu_cars',
    'Diesel freight train EU chemicals': 'diesel_freight_train_eu_chemicals',
    'Diesel freight train EU coal steel': 'diesel_freight_train_eu_coal_steel',
    'Diesel freight train EU building materials': 'diesel_freight_train_eu_building_materials',
    'Diesel freight train EU manufactured products':
        'diesel_freight_train_eu_manufactured_products',
    'Diesel freight train EU cereals': 'diesel_freight_train_eu_cereals',
    'Diesel freight train EU truck and trailer': 'diesel_freight_train_eu_truck_and_trailer',
    'Diesel freight train EU trailer only': 'diesel_freight_train_eu_trailer_only',
    'Electric freight train EU average': 'electric_freight_train_eu_average',
    'Electric freight train EU container': 'electric_freight_train_eu_container',
    'Electric freight train EU cars': 'electric_freight_train_eu_cars',
    'Electric freight train EU chemicals': 'electric_freight_train_eu_chemicals',
    'Electric freight train EU coal steel': 'electric_freight_train_eu_coal_steel',
    'Electric freight train EU building materials': 'electric_freight_train_eu_building_materials',
    'Electric freight train EU manufactured products':
        'electric_freight_train_eu_manufactured_products',
    'Electric freight train EU cereals': 'electric_freight_train_eu_cereals',
    'Electric freight train EU truck and trailer': 'electric_freight_train_eu_truck_and_trailer',
    'Electric freight train EU trailer only': 'electric_freight_train_eu_trailer_only',
} as const
export const simpleRailTransportModeArray = Object.keys(
    simpleRailTransportModeMappings,
) as (keyof typeof simpleRailTransportModeMappings)[]
export const railTransportModeArray = [
    ...simpleRailTransportModeArray,
    ...complexRailTransportModeArray,
] as const

export const complexInlandWaterwaysTransportModeArray = [] as const
export const simpleInlandWaterwaysTransportModeMappings = {
    'Container vessel convoy': 'inland_waterway_container_vessel_convoy',
    // To avoid breaking backwards compatibility, use the previous name for this transport
    // mode. The FE should display the correct name: `Container vessel, medium (110 m)`
    'Inland container vessel': 'inland_waterway_container_vessel_medium',
    'Container vessel, large (135m)': 'inland_waterway_container_vessel_large',
    'Coupled convoy (163-185m)': 'inland_waterway_coupled_convoy',
    'Motor vessel, extra small (<50m / 650t)': 'inland_waterway_motor_vessel_extra_small',
    'Motor vessel, small (50-80m / 650-1000t)': 'inland_waterway_motor_vessel_small',
    'Motor vessel, medium (85-110m / 1000-2000t)': 'inland_waterway_motor_vessel_medium',
    'Motor vessel, large (>135m / 2000-3000t)': 'inland_waterway_motor_vessel_large',
    'Pushed convoy, small (2 barges)': 'inland_waterway_pushed_convoy_small',
    'Pushed convoy, medium (4-5 barges)': 'inland_waterway_pushed_convoy_medium',
    'Pushed convoy, large (6 barges)': 'inland_waterway_pushed_convoy_large',
    Tanker: 'inland_waterway_tanker',
} as const
export const simpleInlandWaterwaysTransportModeArray = Object.keys(
    simpleInlandWaterwaysTransportModeMappings,
) as (keyof typeof simpleInlandWaterwaysTransportModeMappings)[]

export const inlandWaterwaysTransportModeArray = [
    ...simpleInlandWaterwaysTransportModeArray,
    ...complexInlandWaterwaysTransportModeArray,
] as const

export const transportModePerGroupArray: Record<TransportModeGroup, readonly TransportMode[]> = {
    Sea: seaTransportModeArray,
    Air: airTransportModeArray,
    Road: roadTransportModeArray,
    Rail: railTransportModeArray,
    'Inland waterways': inlandWaterwaysTransportModeArray,
} as const

export const simpleTransportModeMappings = {
    ...simpleSeaTransportModeMappings,
    ...simpleRailTransportModeMappings,
    ...simpleInlandWaterwaysTransportModeMappings,
} as const

export const complexTransportModes = [
    ...complexSeaTransportModeArray,
    ...complexAirTransportModeArray,
    ...complexRoadTransportModeArray,
    ...complexRailTransportModeArray,
    ...complexInlandWaterwaysTransportModeArray,
] as const
export const simpleTransportModes = [
    ...simpleSeaTransportModeArray,
    ...simpleAirTransportModeArray,
    ...simpleRoadTransportModeArray,
    ...simpleRailTransportModeArray,
    ...simpleInlandWaterwaysTransportModeArray,
] as const
export const transportModeArray = [...complexTransportModes, ...simpleTransportModes] as const
export type TransportMode = (typeof transportModeArray)[number]

export function parseTransportMode(value: string): Option<TransportMode> {
    const found = transportModeArray.find((e) => e === value)
    return found === undefined ? None : Some(found)
}

export const routeArray = [
    'Address',
    'Airport code',
    'Port code',
    'Geo coordinates',
    'Distance',
] as const
export type Route = (typeof routeArray)[number]

export type CountryCode = `${ShippingCountryCode}`
export const countryCodeArray: CountryCode[] = Object.values(ShippingCountryCode)

export type Tradelane = `${TradeLane}`
export const tradelanesArray: Tradelane[] = Object.values(TradeLane)

export type TransitCorridor = `${ApiTransitCorridor}`
export const transitCorridorsArray: TransitCorridor[] = Object.values(ApiTransitCorridor)

export type AircraftType = `${AircraftTypeMethod.aircraft_type}`
export const aircraftTypesArray: AircraftType[] = Object.values(AircraftTypeMethod.aircraft_type)

export const roadVehiclesArray: RoadVehicleType[] = Object.values(RoadVehicleType)

export type RoadFuelType = `${RoadFuel}`
export const roadFuelTypesArray: RoadFuelType[] = Object.values(RoadFuel)

export const distanceUnitsArray = ['km', 'mi', 'nm'] as const
export type DistanceUnit = (typeof distanceUnitsArray)[number]

export type ColumnPropertyMapping<T> = Extract<PropertyMapping<T>, { source: 'column' }>
export type PropertyMapping<T> =
    | {
          source: 'constant'
          value: T
      }
    | {
          source: 'column'
          columnIndex: number
          valueMapping: ValueMappingInformation<T>
      }

export type ValueMappingInformation<T> =
    | { source: 'direct' }
    | { source: 'unit'; unit: T }
    | {
          source: 'mapping'
          mappings: [string, T][]
      }

export type ValueMappingSource = ValueMappingInformation<unknown>['source']

type SimpleMappingsData = Record<(typeof simpleTransportModes)[number], object>

export type ComplexSeaMapping = {
    refrigeration?: PropertyMapping<boolean>
    tradelane?: PropertyMapping<Tradelane>
    vesselImo?: PropertyMapping<string>
    vesselName?: PropertyMapping<string>
    departureOn?: PropertyMapping<string>
    arrivalOn?: PropertyMapping<string>
    /**
     * Mutually exclusive with restrictedTransitCorridor (row level).
     */
    transitCorridor?: PropertyMapping<TransitCorridor>
    /**
     * Mutually exclusive with transitCorridor (row level).
     */
    restrictedTransitCorridor?: PropertyMapping<TransitCorridor>
}

export type ComplexAirMapping = {
    flightNumber?: PropertyMapping<string>
    departureOn?: PropertyMapping<string>
    aircraftType?: PropertyMapping<AircraftType>
}

export type ComplexRoadMapping = {
    fuel?: PropertyMapping<RoadFuelType>
    refrigeration?: PropertyMapping<boolean>
}

// Our train methodology does not have extra properties atm
type ComplexTrainMapping = object

export type TransportModeMappingData = SimpleMappingsData & {
    'Container Ship': ComplexSeaMapping

    'Cargo Plane': ComplexAirMapping
    'Passenger Plane': ComplexAirMapping
    Plane: ComplexAirMapping

    'Inferred truck': ComplexRoadMapping
    Truck: ComplexRoadMapping & {
        // This property exists on previous templates but we have since moved to have these values
        // as their own complex road transport methods. To not break compatibility, it is still
        // supported in the BE but should not be shown in the FE unless needed
        vehicleType?: PropertyMapping<RoadVehicleType>
    }
    'Truck rigid 7.5t': ComplexRoadMapping
    'Truck rigid 12t': ComplexRoadMapping
    'Truck rigid 20t': ComplexRoadMapping
    'Truck rigid 26t': ComplexRoadMapping
    'Truck rigid 32t': ComplexRoadMapping
    'Truck articulated 34t': ComplexRoadMapping
    'Truck articulated 40t': ComplexRoadMapping
    'Truck articulated 44t': ComplexRoadMapping
    'Truck articulated 60t': ComplexRoadMapping
    'Truck articulated 72t': ComplexRoadMapping
    'Electric truck': Pick<ComplexRoadMapping, 'refrigeration'>
    'Electric van': Pick<ComplexRoadMapping, 'refrigeration'>

    Rail: ComplexTrainMapping
    'Electric train': ComplexTrainMapping
    'Diesel train': ComplexTrainMapping

    // All simple methods in inland waterways
}

// Difference between this code and lune-be: on the frontend size only new (potentially
// dynamic) napping type is allowed and old versions are migrated to the new version
// when fetched. So we just use the newest version type and perform migrations before
// using the data see (migrateOldVersionMappings). This allows us to use/edit templates
// with for the old versions which do exist and must be supported (are supported by BE)
export type TransportModeMapping = IDynamicTransportModeMapping

export interface IDynamicTransportModeMapping {
    /**
     * The actual transport mode selection.
     */
    mode: PropertyMapping<TransportMode>
    /**
     * Additional data for the desired transport modes.
     */
    data?: Partial<TransportModeMappingData>
}

export type StaticTransportModeMapping = {
    [K in TransportMode]: { type: K } & TransportModeMappingData[K]
}[TransportMode]

export type RouteMappingData = {
    Address: {
        countryCode?: PropertyMapping<CountryCode>
        streetLine?: PropertyMapping<string>
        city?: PropertyMapping<string>
        postcode?: PropertyMapping<string>
    }
    'Airport code': {
        airportCode?: PropertyMapping<string>
    }
    'Port code': {
        portCode?: PropertyMapping<string>
    }
    'Geo coordinates': {
        lat?: PropertyMapping<number>
        lon?: PropertyMapping<number>
    }
    Distance: {
        distance?: PropertyMapping<DistanceUnit>
    }
}

export type RouteMapping = {
    [K in Route]: { type: K } & RouteMappingData[K]
}[Route]

export type ParameterName =
    | {
          [K in keyof (RouteMappingData & TransportModeMappingData)]: keyof (RouteMappingData &
              TransportModeMappingData)[K]
      }[keyof (RouteMappingData & TransportModeMappingData)]
    | 'load'
    | 'loadSize'
    | 'transportMode'
    | 'mode'
    | 'shippedAt'
    | 'shipmentId'
    | 'shipmentIdAsIdempotencyKey'
    | 'clientAccountName'
    | 'clientAccountCurrency'

const PARAMETER_NAMES = [
    'refrigeration',
    'tradelane',
    'vesselImo',
    'vesselName',
    'arrivalOn',
    'transitCorridor',
    'restrictedTransitCorridor',
    'aircraftType',
    'flightNumber',
    'departureOn',
    'vehicleType',
    'fuel',
    'countryCode',
    'streetLine',
    'city',
    'postcode',
    'airportCode',
    'portCode',
    'lat',
    'lon',
    'distance',
    'load',
    'loadSize',
    'transportMode',
    'mode',
    'shippedAt',
    'shipmentId',
    'shipmentIdAsIdempotencyKey',
    'clientAccountName',
    'clientAccountCurrency',
] as const

// These two assertions keep ParameterName and PARAMETER_NAMES in sync in both directions
assertType<ParameterName>(PARAMETER_NAMES[0])
assertType<(typeof PARAMETER_NAMES)[number]>(undefined as unknown as ParameterName)

export function parseParameterName(value: string): Option<ParameterName> {
    const found = PARAMETER_NAMES.find((e) => e === value)
    return found === undefined ? None : Some(found)
}

/**
 * Represents a one-based or zero-based row number.
 *
 * Construct it with {@link RowNumber.fromOneBased} or {@link RowNumber.fromZeroBased}.
 */
export class RowNumber {
    private constructor(private readonly value: number) {
        if (value < 1) {
            throw new Error(`Row number must be at least 1, got ${value}`)
        }
        if (!Number.isInteger(value)) {
            throw new Error(`Row number must be an integer, got ${value}`)
        }
    }

    /**
     * Constructs `RowNumber` via a 1-based index.
     *
     * @param value A 1-based row number, providing a value lower than 1 or a floating point
     * number is a programming error.
     */
    static fromOneBased(value: number): RowNumber {
        return new RowNumber(value)
    }

    /**
     * Constructs `RowNumber` via a 0-based index.
     *
     * @param value A 0-based row number, providing a value lower than 0 or a floating point
     * number is a programming error.
     */
    static fromZeroBased(value: number): RowNumber {
        return new RowNumber(value + 1)
    }

    /**
     * Returns a RowNumber representing the next row
     */
    nextRow(): RowNumber {
        return RowNumber.fromOneBased(this.value + 1)
    }

    /**
     * Returns a 1-based row number.
     */
    toOneBased(): number {
        return this.value
    }

    /**
     * Returns a 0-based row number.
     */
    toZeroBased(): number {
        return this.value - 1
    }
}

/**
 * Retrieves values from a specific row in the given sheet.
 *
 * @param rowSize - Ensures the returned array has this exact size.
 *    If the row has fewer values, `undefined` is appended to reach the desired length.
 *    If the row has more, extra values at the end are truncated.
 */
export function extractRowValues(
    sheet: MappingDataSheet,
    rowNumber: RowNumber,
    rowSize: number,
): CellValue[] {
    // Note: this `Array.from` is NECESSARY. This is because the exceljs library actually
    // can return sparse arrays if some cells are empty. This makes operations like `map`
    // behave in a somewhat strange manner. Convert to dense array in this manner.
    const realDataArray = Array.from(
        sheet.getRow(rowNumber.toOneBased()).values as CellValue[],
    ).slice(1)
    return realDataArray
        .slice(0, rowSize)
        .concat(Array(Math.max(0, rowSize - realDataArray.length)).fill(undefined))
}

export function detectTableStartColumn(
    sheet: MappingDataSheet,
    headerRowNumber: RowNumber,
): RowNumber {
    const headerRow = sheet.getRow(headerRowNumber.toOneBased())
    let firstPopulatedRow: RowNumber | undefined
    headerRow.eachCell((cell, cellIndex) => {
        if (!firstPopulatedRow && convertCellToString(cell.value, []) !== undefined) {
            firstPopulatedRow = RowNumber.fromOneBased(cellIndex)
        }
    })
    // Default to first cell if no data in the row
    return firstPopulatedRow ?? RowNumber.fromOneBased(1)
}

export function detectTableColumnSize(
    sheet: MappingDataSheet,
    headerRowNumber: RowNumber,
    headerStartColumn: RowNumber,
): number {
    function doesCellHaveValue(r: RowNumber, c: RowNumber): boolean {
        const cell = sheet.findCell(r.toOneBased(), c.toOneBased())
        return cell !== undefined && convertCellToString(cell.value, []) !== undefined
    }

    let currentColumnNumber = headerStartColumn.nextRow()
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    while (true) {
        if (doesCellHaveValue(headerRowNumber, currentColumnNumber)) {
            currentColumnNumber = currentColumnNumber.nextRow()
            continue
        }

        // We have seen cases where the header cell is empty but that is not where the table ends
        // Verify that the there's no data for the column in what we believe is the table limits
        const nrColumns = currentColumnNumber.toOneBased() - headerStartColumn.toOneBased()
        const tableEndRow = detectTableEndRow(
            sheet,
            headerRowNumber,
            nrColumns,
            headerStartColumn,
            sheet.name,
        )

        let tableHasValuesInColumn = false
        const startRow = headerRowNumber.nextRow().toOneBased()
        for (let i = startRow; i < tableEndRow.toOneBased(); i++) {
            if (doesCellHaveValue(RowNumber.fromOneBased(i), currentColumnNumber)) {
                tableHasValuesInColumn = true
                break
            }
        }

        if (tableHasValuesInColumn) {
            currentColumnNumber = currentColumnNumber.nextRow()
        } else {
            return currentColumnNumber.toOneBased() - headerStartColumn.toOneBased()
        }
    }
}

/**
 * Determines the index of the row where the table ends. This is the first row existing after the
 * header row where there is no data in all the columns.
 */
export function detectTableEndRow(
    sheet: MappingDataSheet,
    headerRowNumber: RowNumber,
    tableColumnSize: number,
    headerStartColumn: RowNumber,
    sheetName: string,
): RowNumber {
    const firstDataRow = headerRowNumber.nextRow().toOneBased()
    let currentRowNumber = headerRowNumber.nextRow()
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    while (true) {
        const rowData = extractRowValues(sheet, currentRowNumber, tableColumnSize).slice(
            headerStartColumn.toZeroBased(),
        )
        const isEmpty = rowData.find((e) => e !== undefined) === undefined
        if (isEmpty) {
            // TODO remove this hardcoded stuff for Forto. handle it correctly.
            const finalRow =
                currentRowNumber.toOneBased() + (sheetName === 'FCL horizontal' ? -2 : 0)
            // Lower bound added to ensure hard-coded Forto logic doesn't produce invalid row number
            return RowNumber.fromOneBased(finalRow < firstDataRow ? firstDataRow : finalRow)
        }
        currentRowNumber = currentRowNumber.nextRow()
    }
}

/**
 * Convert an Excel cell to a string representation. The returned value is always trimmed.
 *
 * If the string representation is empty or if equals to any element given in `treatAsEmpty`
 * (after trimming) the result will be `undefined`
 */
// eslint-disable-next-line complexity
export function convertCellToString(
    cell: CellValue,
    treatAsEmpty: string[] = ['-'],
): string | undefined {
    let rawCellValue: string
    if (typeof cell === 'object' && cell && 'richText' in cell) {
        rawCellValue = cell.richText.map((e) => e.text).join('')
    } else if (typeof cell === 'object' && cell && 'result' in cell && cell.result !== undefined) {
        rawCellValue = cell.result.toString()
    } else if (typeof cell === 'object' && cell && 'error' in cell) {
        rawCellValue = cell.error
    } else if (typeof cell === 'object' && cell && 'text' in cell) {
        rawCellValue = cell.text
    } else if (typeof cell === 'string') {
        rawCellValue = cell
    } else if (typeof cell === 'number') {
        rawCellValue = cell.toString()
    } else if (typeof cell === 'boolean') {
        rawCellValue = cell.toString()
    } else if (typeof cell === 'object' && cell && 'toISOString' in cell) {
        rawCellValue = cell.toISOString()
    } else if (typeof cell === 'object' && cell && 'toString' in cell) {
        rawCellValue = cell.toString()
    } else {
        assertType<undefined | null>(cell)
        return undefined
    }

    const cleanedValue = rawCellValue.trim()
    const filteredValue = treatAsEmpty.includes(cleanedValue) ? undefined : cleanedValue
    return filteredValue?.length !== 0 ? filteredValue : undefined
}

/**
 * Cleans user input values, allowing checks via direct string comparison
 **/
export function cleanValue(v: string): string {
    return v.trim().toLowerCase()
}

/**
 * Finds an input in a list of mappings, returning the mapped value. Comparison is done
 * on cleaned values (see above, trimmed and case insensitive) which means that the first
 * match will be returned in case multiples exist.
 **/
export function getMappedValue<T>(mappings: [string, T][], toFind: string): Option<T> {
    const value = mappings.find(([input, _output]) => cleanValue(input) === cleanValue(toFind))
    return value !== undefined ? Some(value[1]) : None
}

// European-style date formats with optional times and separators
const BASE_EUROPEAN = ['DD-MM-YYYY', 'D-M-YYYY', 'DD/MM/YYYY', 'D/M/YYYY', 'DD.MM.YYYY', 'D.M.YYYY']
const TIME_VARIANTS = [
    'HH:mm:ss',
    'H:mm:ss',
    'HH:m:ss',
    'H:m:ss',
    'HH:mm',
    'H:mm',
    'HH:m',
    'H:m',
    '',
]
const DATE_TIME_SEPARATORS = ['T', ' ']
const EUROPEAN_FORMATS = BASE_EUROPEAN.flatMap(
    (base) =>
        DATE_TIME_SEPARATORS.flatMap((sep) =>
            TIME_VARIANTS.map((time) => (time ? `${base}${sep}${time}` : base)),
        ).concat(base), // also include date-only without separator
)

/**
 * Parse a string into a normalized ISO datetime (UTC). This is logic is used to
 * convert to dates. For this logic, dates and datetimes are interchangeable:
 * - If only a date is provided, we treat it as midnight (00:00:00).
 * - If a datetime is provided but only a date is needed, we trim to the date part.
 *
 * The agreed direction is to be permissive: if it looks like a date/datetime, we
 * should accept it.
 *
 * To note that we support non-RFC 3339 dates/timestamps. While this brings
 * ambiguity, the decision was to accept an input if it seems valid. By default
 * we prioritise the European in dates (DD/MM/...) but we default to US dat
 * format (MM/DD/...) as a fallback.
 */
export function toDateTime(rawValue: string): Result<string | undefined, string> {
    const value = rawValue.trim()
    if (!value) {
        return Ok(undefined)
    }

    // Moment library is quite permissive even allowing values like `49`. To make
    // sure we are handling a date make sure the minimum number of chars exist.
    // The minimum acceptable would be D/M/YYYY which is 8 chars
    if (value.length >= 8) {
        // Moment defaults to US-style (MM-DD-YYYY) but we prioritize European-style
        // (DD-MM-YYYY) so let us try and parse the value using that style
        const europeMoment = moment.utc(value, EUROPEAN_FORMATS, true)
        if (europeMoment.isValid()) {
            return Ok(europeMoment.toISOString())
        }

        const m = moment.utc(value.trim())
        if (m.isValid()) {
            return Ok(m.toISOString())
        }
    }

    return Err(`cannot parse as date: ${value}`)
}

export function toDate(v: string): Result<string | undefined, string> {
    return toDateTime(v).map((d) => d?.slice(0, 10))
}

/**
 * Attempts to parse the `input` string as a number using Big.js. Returns `Some` with the value
 * on success, `None` on failure.
 */
function parseBig(input: string): Option<Big> {
    try {
        return Some(Big(input))
    } catch {
        // No need to check what the error is here, only one thing is possible as long as
        // the input is a string.
        return None
    }
}

export function toNumber(v: string): Result<Big, string> {
    // Excel numbers use comma for thousands separator but `Big` does not parse them
    // attempt normal parsing at first and remove commas as a fallback
    return parseBig(v)
        .orElse(() => parseBig(v.replace(/,/g, '')))
        .toResult('cannot parse as number')
}

export function toNonNegativeNumber(v: string): Result<Big, string> {
    const error = 'cannot parse as non-negative number'
    return toNumber(v)
        .mapErr((_e) => error)
        .andThen((v) => (v.gte(0) ? Ok(v) : Err(error)))
}

export function toPositiveNumber(v: string): Result<Big, string> {
    const error = 'cannot parse as positive number'
    return toNumber(v)
        .mapErr((_e) => error)
        .andThen((v) => (v.gt(0) ? Ok(v) : Err(error)))
}

export function toCoordinate(type: 'lat' | 'lon', v: string): Result<Big, string> {
    const error = `cannot parse as ${type === 'lat' ? 'latitude' : 'longitude'}`
    return toNumber(v)
        .mapErr((_e) => error)
        .andThen((v) => {
            const [low, high] = type === 'lat' ? [-90, 90] : [-180, 180]
            return v.gte(low) && v.lte(high) ? Ok(v) : Err(error)
        })
}

export function toVesselImo(v: string): Result<string, string> {
    return /^\d{7}$/.test(v) ? Ok(v) : Err('cannot parse as vessel IMO')
}

export function toTextWithoutDigits(v: string): Result<string, string> {
    return /\d/.test(v) ? Err('cannot parse as text without digits') : Ok(v)
}

export function toLocode(v: string): Result<string, string> {
    return v.length === 5 ? Ok(v.toUpperCase()) : Err('cannot parse as LOCODE')
}

export function toAirportCode(v: string): Result<string, string> {
    const error = 'cannot parse as airport code'
    return toTextWithoutDigits(v)
        .mapErr((_e) => error)
        .andThen((v) => (v.length === 3 || v.length === 4 ? Ok(v) : Err(error)))
}
